/**
 * Asking the browser not to throw a shop's sales away.
 *
 * IndexedDB is "best effort" storage by default. Under storage pressure a
 * browser may evict a whole origin's data without warning and without asking —
 * and on this terminal that data is the outbox: the sales rung up while there
 * was no signal, which exist **nowhere else in the world** until they upload.
 *
 * On a cheap Android tablet with 16 GB and a full photo roll, that is not a
 * theoretical risk. It is the ordinary end of a Saturday.
 *
 * `navigator.storage.persist()` asks for the durable bucket instead, which a
 * browser will not clear without the user explicitly doing it. Chrome grants it
 * silently to an installed PWA; Firefox prompts; Safari grants it on its own
 * heuristics. Nobody is asked twice, and a refusal is not fatal — it is a fact
 * worth showing a shopkeeper, which is why the result is readable.
 *
 * This is a **terminal-setup** concern more than a code one: an installed PWA
 * gets persistence granted where a browser tab may not, and that is the real
 * reason `docs/TERMINAL-SETUP.md` insists on installing rather than bookmarking.
 *
 * Lives in the repository layer rather than under `db/` because the ESLint rule
 * put it there, and the rule is right: a screen asking "is this terminal's data
 * safe" is asking a question about the device, not reaching into a table.
 */

export interface StorageStatus {
  /** Whether the browser has promised not to evict this origin's data. */
  persistent: boolean;
  /** Whether the API exists at all. Absent on some older Android WebViews. */
  supported: boolean;
  /** Bytes in use, where the browser will say. */
  usage?: number;
  /** Bytes available, where the browser will say. */
  quota?: number;
}

/**
 * Ask once, at startup.
 *
 * Deliberately not awaited by the caller: a permission prompt must never sit
 * between a cashier and the till. Whatever the answer, the app works — the
 * difference is only whether the browser may quietly discard unsent sales.
 */
export async function requestPersistentStorage(): Promise<StorageStatus> {
  if (!("storage" in navigator) || !navigator.storage?.persist) {
    return { persistent: false, supported: false };
  }

  try {
    // Already granted? Asking again would re-prompt on Firefox for nothing.
    const already = await navigator.storage.persisted();
    const persistent = already || (await navigator.storage.persist());

    return { persistent, supported: true, ...(await measure()) };
  } catch {
    // A privacy mode, a locked profile, a policy. Not fatal, and not something
    // to put in front of a cashier mid-sale.
    return { persistent: false, supported: true };
  }
}

/** The current position, for the diagnostics screen. Never prompts. */
export async function storageStatus(): Promise<StorageStatus> {
  if (!("storage" in navigator) || !navigator.storage?.persisted) {
    return { persistent: false, supported: false };
  }

  try {
    return {
      persistent: await navigator.storage.persisted(),
      supported: true,
      ...(await measure()),
    };
  } catch {
    return { persistent: false, supported: true };
  }
}

async function measure(): Promise<{ usage?: number; quota?: number }> {
  if (!navigator.storage?.estimate) return {};

  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { ...(usage != null && { usage }), ...(quota != null && { quota }) };
  } catch {
    return {};
  }
}
